import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

// ════════════════════════════════════════════════════════════════════════
//  Page search — ONE search bar in the main nav (components/dashboard/layout/
//  PageSearchBar.jsx) that searches whatever page is open.
//
//  A page calls   const [search, setSearch] = usePageSearch({ placeholder })
//  instead of owning a SearchBar + its own useState. The returned value/setter
//  behave exactly like useState("") — the page keeps filtering with `search`
//  as before — but the text is typed into the nav bar. Pages that run their
//  search on Enter pass `onKeyDown` (it receives the keydown event).
//
//  Several mounted components can register at once (e.g. Products + the tab
//  inside it): the LAST one registered is the one the nav bar talks to, and
//  the previous one takes over again when it unmounts. Each keeps its own text.
//  `enabled: false` = don't register (a component that is embedded in another
//  page that already owns the search).
// ════════════════════════════════════════════════════════════════════════

let nextId = 1;
let entries = []; // registration order; the last one is the active search
let activeSnapshot = null;
const listeners = new Set();

const emit = () => {
  const last = entries[entries.length - 1];
  activeSnapshot = last ? { id: last.id, placeholder: last.placeholder, value: last.value } : null;
  listeners.forEach((l) => l());
};
const find = (id) => entries.find((e) => e.id === id);

export const pageSearchStore = {
  subscribe: (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
  getActive: () => activeSnapshot,
  getValue: (id) => find(id)?.value ?? "",
  setValue: (id, value) => {
    const e = find(id);
    if (!e || e.value === value) return;
    e.value = value;
    emit();
  },
  keyDown: (id, event) => { find(id)?.onKeyDown?.(event); },
  register: (id, placeholder, onKeyDown) => {
    const existing = find(id);
    if (existing) { existing.placeholder = placeholder; existing.onKeyDown = onKeyDown; emit(); return; }
    entries.push({ id, placeholder, onKeyDown, value: "" });
    emit();
  },
  unregister: (id) => {
    const before = entries.length;
    entries = entries.filter((e) => e.id !== id);
    if (entries.length !== before) emit();
  },
};

export function usePageSearch({ placeholder = "Search…", onKeyDown, enabled = true } = {}) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = nextId++;
  const id = idRef.current;

  // Keep the latest key handler without re-registering on every render.
  const keyRef = useRef(onKeyDown);
  keyRef.current = onKeyDown;

  useEffect(() => {
    if (!enabled) return undefined;
    pageSearchStore.register(id, placeholder, (e) => keyRef.current?.(e));
    return undefined;
  }, [id, enabled, placeholder]);
  useEffect(() => () => pageSearchStore.unregister(id), [id]);
  useEffect(() => { if (!enabled) pageSearchStore.unregister(id); }, [id, enabled]);

  const value = useSyncExternalStore(pageSearchStore.subscribe, () => pageSearchStore.getValue(id));
  const setValue = useCallback((v) => pageSearchStore.setValue(id, typeof v === "string" ? v : ""), [id]);
  return [value, setValue];
}
